import { useCallback, useMemo } from "react";
import { useSnackbarLazyQuery, useSnackbarMutation } from "../../hooks";
import { Apartment, BucketAttachment } from "../models";
import { MUTATIONS } from "../mutations";
import { QUERIES } from "../queries";

type UseApartmentOpts = {
  onApartmentsReportCreated?: (attachment: BucketAttachment) => void;
  onSetShouldApartmentCompleteSurvey?: () => void;
};

export function useApartment(opts?: UseApartmentOpts) {
  const [
    fetchApartmentsList,
    {
      data: apartmentsListData,
      loading: apartmentsListLoading,
      error: apartmentsListError,
      refetch: refetchApartmentsList,
    },
  ] = useSnackbarLazyQuery(QUERIES.APARTMENTS);

  const apartmentsList: Apartment[] = useMemo(() => {
    if (apartmentsListData) {
      return apartmentsListData.apartments;
    }

    return [];
  }, [apartmentsListData]);

  // -- create survey apartment report
  const [
    invokeCreateApartmentSurveysReport,
    {
      loading: createApartmentSurveysReportLoading,
      error: createApartmentSurveysReportError,
    },
  ] = useSnackbarMutation(MUTATIONS.CREATE_APARTMENT_SURVEYS_REPORT, {
    onCompleted: (data) => {
      if (opts?.onApartmentsReportCreated) {
        opts.onApartmentsReportCreated(
          data.createApartmentSurveysReport as BucketAttachment
        );
      }
    },
  });

  const createApartmentSurveysReport = useCallback(
    (buildingId?: string) => {
      invokeCreateApartmentSurveysReport({
        variables: {
          buildingId,
        },
      });
    },
    [invokeCreateApartmentSurveysReport]
  );

  // -- set apartment should fill out the survey.
  const [
    invokeSetApartmentShouldCompleteSurvey,
    {
      loading: setApartmentShouldCompleteSurveyLoading,
      error: setApartmentShouldCompleteSurveyError,
    },
  ] = useSnackbarMutation(MUTATIONS.SET_APARTMENT_SHOULD_COMPLETE_SURVEY, {
    onCompleted: (data) => {
      if (opts?.onSetShouldApartmentCompleteSurvey) {
        opts.onSetShouldApartmentCompleteSurvey();
      }
    },
  });

  const setApartmentShouldCompleteSurvey = useCallback(
    (apartmentId: string, shouldCompleteSurvey: boolean) => {
      invokeSetApartmentShouldCompleteSurvey({
        variables: {
          apartmentId,
          shouldCompleteSurvey,
        },
      });
    },
    [invokeSetApartmentShouldCompleteSurvey]
  );

  return {
    fetchApartmentsList,
    refetchApartmentsList,
    apartmentsList,
    apartmentsListLoading,
    apartmentsListError,
    createApartmentSurveysReport,
    createApartmentSurveysReportLoading,
    createApartmentSurveysReportError,
    setApartmentShouldCompleteSurvey,
    setApartmentShouldCompleteSurveyLoading,
    setApartmentShouldCompleteSurveyError,
  };
}
